/**
 * Grupos com regra própria — pedido da Mariana (ela, 05/10/2026), pensado para o Natal: uma lista de
 * MLBs (colada de uma vez) com pisos próprios — entrar/trocar/seletor, relâmpago e saída — e campanhas
 * de cupom EXCLUSIVAS (só entram nos MLBs do grupo e não entram fora dele).
 * Ela, 05/10/2026 (tarde): o grupo só recebe os cupons marcados aqui (vazio = nenhum cupom); cupom geral
 * já ativo num MLB do grupo sai pelo robô.
 * Precedência: exceção vigente > grupo vigente > regra geral. Vencido, o grupo volta à regra geral.
 * Gravado em grupos_motor.json pelo serviço (X-Token só no servidor); salvar roda o motor UMA vez.
 * Mesmas convenções do painel ExcecoesRobo (ao lado, na aba Aprovações).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Button, Checkbox, Collapse, FormControlLabel, Stack, TextField, Typography } from '@mui/material'
import { fetchCuponsDaConta, fetchExcecoes, fetchGrupos, fetchPreviaGrupo, removerGrupo, removerMlbDoGrupo, rodarMotor, salvarGrupo, type MotorDoGrupo, fetchCampanhasDaConta } from './promocoesService'
import { EVENTO_GRUPO, parseMlbs, rotuloCampanha, rotuloPisos, slugGrupo, type CampanhaConta, type CupomConta, type Grupo, type PreviaGrupo } from './logic'
import { usePromo } from './ctx'
import { LARANJA, P, Panel, Pill, brl, mono, quando } from './ui'

/** Prazo padrão do grupo (ela, 05/10/2026): 60 dias. */
const PRAZO_PADRAO = 60

const hojeMais = (dias: number) => {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

const ddmmaaaa = (iso: string) => {
  const [a, m, d] = iso.split('-')
  return `${d}/${m}/${a}`
}

/** '' → null; '7,5' → 7.5; lixo → NaN (o formulário mostra o erro). */
const numOuNulo = (s: string): number | null => {
  const t = s.trim().replace(',', '.')
  if (t === '') return null
  return Number(t)
}
const pisoValido = (v: number | null) => v == null || (Number.isFinite(v) && v >= 0 && v <= 100)

/** Selo 🎄 do grupo, usado nas linhas da fila e no cabeçalho de Por anúncio. */
export function SeloGrupo({ nome }: { nome: string }) {
  return <Pill label={`🎄 ${nome}`} tone="cand" />
}

/** Fecho das mensagens: o grupo é gravado mesmo que o motor falhe — aí a fila NÃO reflete o grupo (onda final, 05/10/2026). */
const motorTxt = (m?: MotorDoGrupo, gravado = 'grupo gravado') =>
  m && m.ok === false ? ` — ${gravado}, mas o motor FALHOU — rode 'Reler a API e rodar o motor'` : ' — o motor já rodou'

type Form = {
  id?: string
  nome: string
  texto: string
  piso: string
  pisoRelampago: string
  pisoSaida: string
  /** grupos v2 (ela, 09/10/2026): piso do seletor (vazio = piso) e campanhas do grupo */
  pisoSeletor: string
  campanhas: string[]
  ate: string
  cupons: string[]
  motivo: string
  /** Lista fixa da relâmpago diária (ela, 21/10/2026): o robô liga a relâmpago só nos MLBs dos grupos marcados, na ordem digitada. */
  relampagoDiaria: boolean
  /** Liquidação / catálogo / estoque parado (ela, 23/10/2026): exceção do item 5 — a candidata entra com MC ≥ piso sem precisar superar a vencedora atual. */
  liquidacao: boolean
  /** Preço-alvo do grupo em R$ (ela, 25-26/10/2026): teto do preço ao comprador; cada MLB pode ter o seu na linha (`MLB;14,80`). */
  precoAlvo: string
  /** Proteção contra queima (R$): opção abaixo disto não entra. */
  precoMinimo: string
  /** Tolerância acima do alvo (%); vazio = a geral dos Parâmetros (1,5 %). */
  toleranciaAcima: string
  /** Desconto individual que não foi o robô que criou nunca sai (padrão ligado). */
  respeitarManual: boolean
  /** Relâmpago só com a flag (a lista fixa implica ligada). */
  permitirRelampago: boolean
}

// piso começa VAZIO (obrigatório): ela escolhe o número do grupo, a tela não sugere um (onda final, 05/10/2026)
// 05/10/2026 (ela, tarde): grupo sem cupom fica sem cupom nenhum — avisa, não bloqueia
const AVISO_SEM_CUPOM = 'Atenção: sem cupom escolhido, os anúncios do grupo ficam sem cupom nenhum e os cupons gerais ativos neles serão removidos pelo robô'

const formVazio = (): Form => ({ nome: '', texto: '', piso: '', pisoRelampago: '', pisoSaida: '', pisoSeletor: '', campanhas: [], ate: hojeMais(PRAZO_PADRAO), cupons: [], motivo: '', relampagoDiaria: false, liquidacao: false, precoAlvo: '', precoMinimo: '', toleranciaAcima: '', respeitarManual: true, permitirRelampago: false })

/** '14,80' → '14,80' para a linha do MLB ao editar (mesmo formato que ela digita). */
const precoTxt = (v: number | null | undefined) => (v == null ? '' : String(v).replace('.', ','))

export function GruposRegra() {
  const ctx = usePromo()
  const [grupos, setGrupos] = useState<Grupo[] | null>(null)
  const [cupons, setCupons] = useState<CupomConta[]>([])
  const [campanhas, setCampanhas] = useState<CampanhaConta[]>([])
  const [emExcecao, setEmExcecao] = useState<Set<string>>(new Set())
  const [form, setForm] = useState<Form>(formVazio)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [aberto, setAberto] = useState<Set<string>>(new Set())
  // remover o grupo inteiro pede um segundo clique (sem window.confirm): id do grupo armado
  const [armado, setArmado] = useState<string | null>(null)
  // prévia por MLB do grupo aberto (ela, 25/10/2026, 2.7): a última fila do motor, por MLB
  const [previa, setPrevia] = useState<Record<string, PreviaGrupo | 'carregando' | 'erro'>>({})

  // grupos_motor.json ilegível no servidor (onda final, 05/10/2026): o serviço devolve ok:false + erro
  const [erroLeitura, setErroLeitura] = useState<string | null>(null)

  const carregar = useCallback(() => {
    void fetchGrupos().then((d) => {
      setErroLeitura(d && !d.ok ? d.erro || 'grupos_motor.json ilegível' : null)
      setGrupos(d && d.ok ? d.grupos : null)
    })
    void fetchExcecoes().then((d) => setEmExcecao(new Set((d?.excecoes ?? []).filter((x) => x.estado !== 'vencida').map((x) => x.mlb))))
  }, [])
  useEffect(() => {
    carregar()
  }, [carregar])
  useEffect(() => {
    let vivo = true // trocou de conta antes da resposta: descarta a da conta anterior
    void fetchCuponsDaConta(ctx.contaId).then((cs) => {
      if (vivo) setCupons(cs)
    })
    void fetchCampanhasDaConta(ctx.contaId).then((cs) => {
      if (vivo) setCampanhas(cs)
    })
    return () => {
      vivo = false
    }
  }, [ctx.contaId])

  // MLB vindo de outra aba: entra no fim da caixa (não apaga a lista que ela está montando)
  useEffect(() => {
    const h = (e: Event) => {
      const det = (e as CustomEvent<{ mlb: string; titulo?: string }>).detail
      const mlb = (det?.mlb || '').trim().toUpperCase()
      if (!mlb) return
      setForm((f) => (parseMlbs(f.texto).validos.includes(mlb) ? f : { ...f, texto: f.texto.trim() ? `${f.texto.trimEnd()}\n${mlb}` : mlb }))
      setMsg(null)
      document.getElementById('promo-grupos')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    window.addEventListener(EVENTO_GRUPO, h)
    return () => window.removeEventListener(EVENTO_GRUPO, h)
  }, [])

  const lista = useMemo(() => grupos ?? [], [grupos])
  const parsed = useMemo(() => parseMlbs(form.texto), [form.texto])
  // o grupo que está sendo editado: pelo id (editar) ou, digitando, pelo MESMO slug que o serviço usa como id
  // ("Nátal 2026" e "natal 2026" são o mesmo grupo natal-2026 — onda final, 05/10/2026)
  const atual = useMemo(() => {
    const alvo = form.id || slugGrupo(form.nome)
    return alvo ? lista.find((g) => g.id === alvo) ?? null : null
  }, [lista, form.id, form.nome])
  const resumo = useMemo(() => {
    const doAtual = new Set((atual?.mlbs ?? []).map((m) => m.mlb))
    const outro = new Map<string, string>()
    // grupo vencido não conta (o serviço só considera os vigentes)
    for (const g of lista) if (g.id !== atual?.id && g.estado !== 'vencido') for (const m of g.mlbs) if (!outro.has(m.mlb)) outro.set(m.mlb, g.nome)
    const jaNeste = parsed.validos.filter((m) => doAtual.has(m))
    const emOutro = parsed.validos.filter((m) => outro.has(m))
    const nomesOutros = [...new Set(emOutro.map((m) => outro.get(m) as string))]
    const excecao = parsed.validos.filter((m) => emExcecao.has(m))
    const saem = atual ? [...doAtual].filter((m) => !parsed.validos.includes(m)) : []
    return { jaNeste, emOutro, nomesOutros, excecao, saem }
  }, [parsed, lista, atual, emExcecao])

  // ela, 15/10/2026: o mesmo cupom pode estar em mais de um grupo vigente (ex.: matriz e Extrema) — a tela só
  // informa onde ele já está; o serviço não recusa mais. Grupo vencido não conta.
  const outrosDoCupom = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const g of lista) if (g.id !== atual?.id && g.estado !== 'vencido') for (const c of g.cupons) m.set(c, [...(m.get(c) ?? []), g.nome])
    return m
  }, [lista, atual])
  // opções = campanhas da conta + as que o grupo já tem e não vieram na lista (outra conta): editar não pode apagá-las calado
  const opcoes = useMemo(() => {
    const ids = new Set(cupons.map((c) => c.id))
    const extras: CupomConta[] = form.cupons
      .filter((id) => !ids.has(id))
      .map((id) => {
        const i = atual?.cupons.indexOf(id) ?? -1
        return { id, nome: (i >= 0 ? atual?.cupons_nomes[i] : undefined) || id, conta: '', n_ativa: 0, n_candidata: 0, n_ads: 0 }
      })
    return [...cupons, ...extras]
  }, [cupons, form.cupons, atual])

  // campanhas do grupo: opções da conta + as que o grupo já tem e não vieram na lista (outra conta / campanha que sumiu da base)
  const opcoesCampanhas = useMemo(() => {
    const ids = new Set(campanhas.map((c) => c.id))
    const extras: CampanhaConta[] = form.campanhas
      .filter((id) => !ids.has(id))
      .map((id) => {
        const i = atual?.campanhas?.indexOf(id) ?? -1
        return { id, nome: (i >= 0 ? atual?.campanhas_nomes?.[i] : undefined) || id, type: '', conta: '', start_date: null, finish_date: null, n_ativa: 0, n_candidata: 0, n_ads: 0 }
      })
    return [...campanhas, ...extras]
  }, [campanhas, form.campanhas, atual])

  const piso = numOuNulo(form.piso)
  const pisoRel = numOuNulo(form.pisoRelampago)
  const pisoSai = numOuNulo(form.pisoSaida)
  const pisoSel = numOuNulo(form.pisoSeletor)
  const erroPiso = piso == null ? 'obrigatório' : !pisoValido(piso) ? 'de 0 a 100' : null
  const erroRel = !pisoValido(pisoRel) ? 'de 0 a 100' : null
  const erroSel = !pisoValido(pisoSel) ? 'de 0 a 100' : null
  // mesma regra do serviço (core/grupos.py): saída ≤ piso e ≤ piso do seletor
  const erroSai = !pisoValido(pisoSai) ? 'de 0 a 100'
    : pisoSai != null && piso != null && pisoSai > piso ? 'piso de saída deve ser ≤ piso'
    : pisoSai != null && pisoSel != null && pisoSai > pisoSel ? 'piso de saída deve ser ≤ piso do seletor' : null
  const alvo = numOuNulo(form.precoAlvo)
  const minimo = numOuNulo(form.precoMinimo)
  const tol = numOuNulo(form.toleranciaAcima)
  const erroAlvo = alvo != null && !(Number.isFinite(alvo) && alvo > 0) ? 'R$ maior que zero' : null
  const erroMinimo = minimo != null && !(Number.isFinite(minimo) && minimo >= 0) ? 'R$ inválido' : minimo != null && alvo != null && minimo > alvo ? 'deve ser ≤ preço-alvo' : null
  const erroTol = tol != null && !(Number.isFinite(tol) && tol >= 0 && tol <= 20) ? 'de 0 a 20' : null
  const podeSalvar = ctx.escreve && !busy && parsed.validos.length > 0 && form.nome.trim() !== '' && form.piso.trim() !== '' && !erroPiso && !erroRel && !erroSel && !erroSai && !erroAlvo && !erroMinimo && !erroTol && Boolean(form.ate)

  const titulos = (mlbs: string[]) => {
    const t: Record<string, string> = {}
    for (const m of mlbs) {
      const x = ctx.conta.ads.find((a) => a.mlb === m)
      if (x?.titulo) t[m] = x.titulo
    }
    return t
  }

  const msgSalvo = (r: Awaited<ReturnType<typeof salvarGrupo>>, nome: string, ate: string) => {
    const renovados = r.gravados.filter((g) => g.acao === 'renovada').length
    let s = `${r.gravados.length} MLBs gravados no grupo "${r.grupo?.nome || nome}" até ${ddmmaaaa(r.grupo?.ate || ate)} (${renovados} renovados)${motorTxt(r.motor)}`
    if (r.invalidos?.length) s += ` · ignorados (inválidos): ${r.invalidos.join(', ')}`
    if (r.avisos?.length) s += ` · ⚠ ${r.avisos.join(' · ')}`
    return s
  }

  const salvar = async () => {
    if (!podeSalvar || piso == null) return
    setBusy(true)
    setMsg(null)
    const nome = form.nome.trim()
    try {
      const r = await salvarGrupo({
        id: form.id, nome, motivo: form.motivo.trim(), ate: form.ate, piso, piso_relampago: pisoRel, piso_saida: pisoSai,
        piso_seletor: pisoSel, campanhas: form.campanhas, relampago_diaria: form.relampagoDiaria, liquidacao: form.liquidacao,
        preco_alvo: alvo, preco_minimo: minimo, tolerancia_acima: tol, respeitar_desconto_manual: form.respeitarManual, permitir_relampago: form.permitirRelampago,
        // o texto colado vai inteiro: o serviço lê o preço-alvo por MLB (`MLB;14,80`) — 25/10/2026
        cupons: form.cupons, mlbs: form.texto, titulos: titulos(parsed.validos), conta: ctx.contaId,
      })
      if (!r.ok) {
        setMsg('o serviço não gravou o grupo')
        return
      }
      setMsg(msgSalvo(r, nome, form.ate) + (parsed.invalidos.length && !r.invalidos?.length ? ` · ignorados (inválidos): ${parsed.invalidos.join(', ')}` : '')
        + (form.cupons.length ? '' : ` · ⚠ ${AVISO_SEM_CUPOM}`))
      setForm(formVazio())
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const editar = (g: Grupo) => {
    setForm({
      id: g.id, nome: g.nome, texto: g.mlbs.map((m) => (m.preco_alvo != null ? `${m.mlb};${precoTxt(m.preco_alvo)}` : m.mlb)).join('\n'), piso: String(g.piso),
      pisoRelampago: g.piso_relampago == null ? '' : String(g.piso_relampago), pisoSaida: g.piso_saida == null ? '' : String(g.piso_saida),
      pisoSeletor: g.piso_seletor == null ? '' : String(g.piso_seletor), campanhas: [...(g.campanhas ?? [])],
      ate: g.ate, cupons: [...g.cupons], motivo: g.motivo || '', relampagoDiaria: Boolean(g.relampago_diaria), liquidacao: Boolean(g.liquidacao),
      precoAlvo: precoTxt(g.preco_alvo), precoMinimo: precoTxt(g.preco_minimo), toleranciaAcima: precoTxt(g.tolerancia_acima),
      respeitarManual: g.respeitar_desconto_manual !== false, permitirRelampago: Boolean(g.permitir_relampago),
    })
    setMsg(`editando "${g.nome}": a lista abaixo SUBSTITUI a do grupo ao salvar`)
    document.getElementById('promo-grupos')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // renovar = regrava o grupo como está, com prazo de hoje + 60 dias
  const renovar = async (g: Grupo) => {
    setBusy(true)
    setMsg(null)
    const ate = hojeMais(PRAZO_PADRAO)
    try {
      const mlbs = g.mlbs.map((m) => m.mlb)
      const r = await salvarGrupo({
        id: g.id, nome: g.nome, motivo: g.motivo, ate, piso: g.piso, piso_relampago: g.piso_relampago, piso_saida: g.piso_saida,
        piso_seletor: g.piso_seletor ?? null, campanhas: g.campanhas ?? [], relampago_diaria: Boolean(g.relampago_diaria), liquidacao: Boolean(g.liquidacao),
        preco_alvo: g.preco_alvo ?? null, preco_minimo: g.preco_minimo ?? null, tolerancia_acima: g.tolerancia_acima ?? null,
        respeitar_desconto_manual: g.respeitar_desconto_manual !== false, permitir_relampago: Boolean(g.permitir_relampago),
        // renovar mantém o preço-alvo de cada MLB (o serviço preserva o anterior quando a linha vem sem preço)
        cupons: g.cupons, mlbs, titulos: Object.fromEntries(g.mlbs.filter((m) => m.titulo).map((m) => [m.mlb, m.titulo as string])), conta: ctx.contaId,
      })
      setMsg(r.ok ? msgSalvo(r, g.nome, ate) : 'o serviço não renovou o grupo')
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const remover = async (g: Grupo) => {
    if (armado !== g.id) {
      setArmado(g.id)
      return
    }
    setArmado(null)
    setBusy(true)
    try {
      const r = await removerGrupo(g.id)
      setMsg(`grupo "${g.nome}" removido — os ${g.n_mlbs} MLBs voltaram à regra geral${motorTxt(r.motor, 'remoção gravada')}`)
      if (form.id === g.id) setForm(formVazio())
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const tirar = async (g: Grupo, mlb: string) => {
    setBusy(true)
    try {
      const r = await removerMlbDoGrupo(g.id, mlb)
      setMsg(`${mlb} saiu do grupo "${g.nome}" — volta à regra geral${motorTxt(r.motor, 'remoção gravada')}`)
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const avisos = lista.filter((g) => g.estado !== 'vigente')
  const corEstado = (g: Grupo) => (g.estado === 'vencido' ? 'error.main' : g.estado === 'vencendo' ? LARANJA : 'text.secondary')
  const rotuloEstado = (g: Grupo) =>
    g.estado === 'vencido' ? `VENCIDO há ${Math.abs(g.dias_restantes ?? 0)} dia(s) — regra geral` : g.estado === 'vencendo' ? `vence em ${g.dias_restantes} dia(s)` : `até ${ddmmaaaa(g.ate)} (${g.dias_restantes} dias)`
  const tituloDe = (m: { mlb: string; titulo?: string }) => m.titulo || ctx.conta.ads.find((a) => a.mlb === m.mlb)?.titulo || ''
  const toggle = (id: string) => setAberto((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  // prévia por MLB (ela, 25/10/2026): carrega ao abrir o grupo; "Atualizar agora" roda o motor e relê
  const carregarPrevia = useCallback(async (id: string) => {
    if (!ctx.escreve) return
    setPrevia((p) => ({ ...p, [id]: 'carregando' }))
    try {
      const r = await fetchPreviaGrupo(id, ctx.contaId)
      setPrevia((p) => ({ ...p, [id]: r }))
    } catch {
      setPrevia((p) => ({ ...p, [id]: 'erro' }))
    }
  }, [ctx.escreve, ctx.contaId])
  useEffect(() => {
    for (const id of aberto) if (!(id in previa)) void carregarPrevia(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto])
  useEffect(() => { setPrevia({}) }, [ctx.contaId, ctx.fila?.gerado_em])
  const atualizarPrevia = async (id: string) => {
    setBusy(true)
    try {
      await rodarMotor()
      await carregarPrevia(id)
    } finally {
      setBusy(false)
    }
  }
  const rotuloRegra = (r: string | null | undefined) =>
    r === 'preco_alvo' ? '🎯 preço-alvo' : r === 'rebate_novo' ? '⚡ rebate novo' : r === 'manual' ? '✋ desconto manual' : r === 'troca_encarece' ? '⛔ encarece'
      : r === 'cupom_nao_liberado' ? '🎟 cupom não liberado' : r === 'fora_da_lista' ? 'fora da lista' : r === 'relampago_desligada' ? 'relâmpago desligada' : r === 'rebate' ? 'rebate' : r || ''
  const precoDe = (x: { tipo: string | null; preco: number | null } | null | undefined) => (x ? `${x.tipo ?? ''}${x.preco != null ? ` ${brl(x.preco)}` : ''}` : '—')

  return (
    <Box id="promo-grupos">
      <Panel
        title="🎄 Grupos com regra própria — pisos e cupom exclusivo por lista de MLBs"
        tone={avisos.length ? 'alert' : undefined}
        count={grupos ? `${lista.length} ${lista.length === 1 ? 'grupo' : 'grupos'}${avisos.length ? ` · ${avisos.length} vencendo/vencido(s)` : ''}` : undefined}
      >
        <P>
          Lista de MLBs com pisos próprios (entrar, seletor, relâmpago e saída) e cupons exclusivos. Exceção vigente vale acima do grupo.
          Vazio em 'saída' = nunca sai, só alerta abaixo do piso. No grupo não há conta de 3 vagas nem troca; o seletor desce até o piso do seletor sem teto (09/10/2026).
          Desde 23/10/2026: cupom do grupo primeiro; candidata só entra se a M.C. supera a da vencedora atual (a ativa mais barata); sem rebate não entra a menos de 3 % abaixo do preço de uma campanha com rebate ativa; ao aceitar rebate, a promoção sem rebate mais barata sai. As campanhas marcadas como do grupo entram mesmo futuras, mas respeitam todos os pisos como qualquer outra.
          {' '}Desde 25-26/10/2026: <b>preço-alvo</b> por grupo ou por MLB (teto do preço ao comprador, + tolerância); o robô mantém o desconto individual no alvo e só troca por campanha que não encarece (até a tolerância, com mais MC em R$) ou por rebate novo mais barato com margem equivalente; <b>desconto manual</b> nunca sai; relâmpago só com a flag; DEAL/oferta só se estiver nas campanhas do grupo.
          {' '}Cole os MLBs (um por linha, ou separados por vírgula/espaço); prazo padrão {PRAZO_PADRAO} dias. Salvar grava tudo de uma vez e roda o motor uma vez.
          {erroLeitura ? <><br /><Box component="b" sx={{ color: 'error.main' }}>⛔ {erroLeitura}. Enquanto isso o motor não aplica grupo nenhum e o robô não mexe em cupom nem em saída.</Box></>
            : grupos == null ? <><br /><b>Não consegui ler os grupos do serviço.</b></> : null}
          {!ctx.escreve ? <><br /><b>Serviço fora do ar ou em preview: só leitura.</b></> : null}
        </P>

        {avisos.length ? (
          <Stack spacing={0.25} sx={{ mb: 1 }}>
            {avisos.map((g) => (
              <Typography key={g.id} sx={{ fontSize: 12.5, color: corEstado(g) }}>
                ⚠ <b>{g.nome}</b> ({g.n_mlbs} MLBs) — {rotuloEstado(g)}
              </Typography>
            ))}
          </Stack>
        ) : null}

        {/* formulário (sempre aberto, como o das exceções) */}
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
            <TextField size="small" label="Nome do grupo" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Natal 2026" sx={{ minWidth: 220 }} helperText={form.id ? `editando o grupo existente (${form.id})` : atual ? 'já existe um grupo com esse nome: salvar substitui a lista dele' : ' '} disabled={Boolean(form.id)} />
            <TextField size="small" label="Piso (%)" value={form.piso} onChange={(e) => setForm((f) => ({ ...f, piso: e.target.value }))} sx={{ width: 120 }} error={Boolean(erroPiso)} helperText={erroPiso ?? 'aceite de proposta (preço do ML)'} />
            <TextField size="small" label="Piso do seletor (%)" value={form.pisoSeletor} onChange={(e) => setForm((f) => ({ ...f, pisoSeletor: e.target.value }))} sx={{ width: 150 }} error={Boolean(erroSel)} helperText={erroSel ?? 'vazio = piso; desce até aqui, sem teto'} />
            <TextField size="small" label="Piso relâmpago (%)" value={form.pisoRelampago} onChange={(e) => setForm((f) => ({ ...f, pisoRelampago: e.target.value }))} sx={{ width: 150 }} error={Boolean(erroRel)} helperText={erroRel ?? 'vazio = usa o piso'} />
            <TextField size="small" label="Piso de saída (%)" value={form.pisoSaida} onChange={(e) => setForm((f) => ({ ...f, pisoSaida: e.target.value }))} sx={{ width: 150 }} error={Boolean(erroSai)} helperText={erroSai ?? 'vazio = nunca sai'} />
            <TextField size="small" type="date" label="Manter até" value={form.ate} onChange={(e) => setForm((f) => ({ ...f, ate: e.target.value }))} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: hojeMais(1) } }} helperText="depois volta à regra geral" />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
            <TextField size="small" label="Preço-alvo do grupo (R$)" value={form.precoAlvo} onChange={(e) => setForm((f) => ({ ...f, precoAlvo: e.target.value }))} sx={{ width: 190 }} error={Boolean(erroAlvo)} helperText={erroAlvo ?? 'teto do preço ao comprador; cada MLB pode ter o seu na linha (MLB;14,80)'} />
            <TextField size="small" label="Preço mínimo (R$)" value={form.precoMinimo} onChange={(e) => setForm((f) => ({ ...f, precoMinimo: e.target.value }))} sx={{ width: 160 }} error={Boolean(erroMinimo)} helperText={erroMinimo ?? 'opção abaixo disto não entra'} />
            <TextField size="small" label="Tolerância acima do alvo (%)" value={form.toleranciaAcima} onChange={(e) => setForm((f) => ({ ...f, toleranciaAcima: e.target.value }))} sx={{ width: 200 }} error={Boolean(erroTol)} helperText={erroTol ?? 'vazio = a geral (Parâmetros, 1,5 %)'} />
          </Stack>
          <TextField
            size="small" multiline minRows={3} maxRows={8} label="MLBs do grupo" value={form.texto}
            onChange={(e) => setForm((f) => ({ ...f, texto: e.target.value }))}
            placeholder={'MLB1234567890;14,80\nMLB2345678901, MLB3456789012'}
            helperText="um por linha, ou separados por vírgula/espaço; `MLB;preço` dá o preço-alvo daquele MLB (vence o do grupo) — 25/10/2026"
            slotProps={{ htmlInput: { style: { ...mono, fontSize: 12.5 } } }}
          />
          {form.texto.trim() ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: -0.5 }}>
              <b>{parsed.validos.length} válidos</b>
              {' · '}
              <Box component="span" sx={{ color: parsed.invalidos.length ? 'error.main' : undefined }}>
                {parsed.invalidos.length} inválidos{parsed.invalidos.length ? ` (${parsed.invalidos.slice(0, 10).join(', ')}${parsed.invalidos.length > 10 ? '…' : ''})` : ''}
              </Box>
              {Object.keys(parsed.precos).length ? <>{' · '}<b>{Object.keys(parsed.precos).length} com preço-alvo na linha</b></> : null}
              {' · '}{resumo.jaNeste.length} já neste grupo
              {' · '}
              <Box component="span" sx={{ color: resumo.emOutro.length ? LARANJA : undefined }}>
                {resumo.emOutro.length} em outro grupo{resumo.nomesOutros.length ? ` (${resumo.nomesOutros.join(', ')} — vale o de prazo mais longo)` : ''}
              </Box>
              {resumo.excecao.length ? <Box component="span" sx={{ color: LARANJA }}>{' · '}{resumo.excecao.length} em exceção (a exceção vale acima do grupo)</Box> : null}
              {resumo.saem.length ? <Box component="span" sx={{ color: LARANJA }}>{' · '}{resumo.saem.length} saem do grupo ao salvar</Box> : null}
            </Typography>
          ) : null}
          <Autocomplete
            multiple size="small" options={opcoesCampanhas} sx={{ minWidth: 260 }}
            value={opcoesCampanhas.filter((c) => form.campanhas.includes(c.id))}
            onChange={(_, v) => setForm((f) => ({ ...f, campanhas: v.map((c) => c.id) }))}
            getOptionLabel={(c) => rotuloCampanha(c)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, c) => {
              const { key, ...rest } = props as typeof props & { key: string }
              return (
                <li key={key} {...rest}>
                  <Box sx={{ fontSize: 13 }}>{rotuloCampanha(c)}{c.conta ? ` · ${c.n_candidata} candidatos · ${c.n_ativa} ativas` : ''}</Box>
                </li>
              )
            }}
            noOptionsText="nenhuma campanha de disputa nesta conta"
            renderInput={(params) => <TextField {...params} label="Campanhas do grupo" helperText="entram mesmo futuras (ficam esperando); respeitam todos os pisos — entrada, saída e rebate — como qualquer outra (23/10/2026)" />}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
            <Autocomplete
              multiple size="small" options={opcoes} sx={{ flex: 1, minWidth: 260 }}
              value={opcoes.filter((c) => form.cupons.includes(c.id))}
              // até 2 cupons por grupo (ela, 23/10/2026; antes um só): ficam os 2 últimos escolhidos
              onChange={(_, v) => setForm((f) => ({ ...f, cupons: v.slice(-2).map((c) => c.id) }))}
              getOptionLabel={(c) => (c.conta ? `${c.nome} · ${c.n_candidata} candidatos` : c.nome)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, c) => {
                const { key, ...rest } = props as typeof props & { key: string }
                return (
                  <li key={key} {...rest}>
                    <Box sx={{ fontSize: 13 }}>
                      {c.nome} · {c.n_candidata} candidatos
                      {outrosDoCupom.has(c.id) ? <Box component="span" sx={{ color: 'text.secondary' }}> — também no grupo {outrosDoCupom.get(c.id)?.map((n) => `"${n}"`).join(', ')}</Box> : null}
                    </Box>
                  </li>
                )
              }}
              noOptionsText="nenhuma campanha de cupom nesta conta"
              renderInput={(params) => <TextField {...params} label="Cupons do grupo" helperText="até 2 cupons (23/10/2026): o grupo só recebe esses cupons e a margem dos anúncios do grupo é calculada no preço com o cupom aplicado (com 2, o de maior percentual — pior caso; desconto real: % com teto em R$); vazio = nenhum cupom entra nestes anúncios. O mesmo cupom pode estar em mais de um grupo (ex.: matriz e Extrema)" />}
            />
            <TextField size="small" label="Motivo (opcional)" value={form.motivo} onChange={(e) => setForm((f) => ({ ...f, motivo: e.target.value }))} placeholder="Natal: margem menor para girar" sx={{ flex: 1, minWidth: 220 }} helperText=" " />
          </Stack>
          {parsed.validos.length && !form.cupons.length ? (
            <Typography sx={{ fontSize: 12, color: LARANJA, mt: -0.5 }}>⚠ {AVISO_SEM_CUPOM}.</Typography>
          ) : null}
          <FormControlLabel
            control={<Checkbox size="small" checked={form.relampagoDiaria} onChange={(e) => setForm((f) => ({ ...f, relampagoDiaria: e.target.checked }))} />}
            label={<span style={{ fontSize: 12.5 }}>⚡ <b>Lista fixa da relâmpago</b> — os MLBs deste grupo vão <b>primeiro</b> na fila da relâmpago, <b>na ordem em que você os digitou acima</b>; quem não fecha o piso de relâmpago é pulado. Desde 23/10/2026 a relâmpago vale para a conta inteira (toda candidata que fecha o piso); a lista fixa só dá a ordem.</span>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={form.respeitarManual} onChange={(e) => setForm((f) => ({ ...f, respeitarManual: e.target.checked }))} />}
            label={<span style={{ fontSize: 12.5 }}>✋ <b>Respeitar desconto manual</b> — desconto individual que <b>não</b> foi o robô que criou (Central de Promoções, API) nunca sai pelo robô; abaixo do piso de saída só alerta. Com preço-alvo, o alvo não se aplica enquanto o desconto manual estiver lá (25/10/2026).</span>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={form.permitirRelampago || form.relampagoDiaria} disabled={form.relampagoDiaria} onChange={(e) => setForm((f) => ({ ...f, permitirRelampago: e.target.checked }))} />}
            label={<span style={{ fontSize: 12.5 }}>⚡ <b>Permitir relâmpago</b> — desligado, nenhuma relâmpago entra nos MLBs deste grupo (a lista fixa acima implica ligado). Com preço-alvo, relâmpago acima do teto não entra (25/10/2026).</span>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={form.liquidacao} onChange={(e) => setForm((f) => ({ ...f, liquidacao: e.target.checked }))} />}
            label={<span style={{ fontSize: 12.5 }}>🔥 <b>Liquidação / catálogo / estoque parado</b> — exceção da regra "só ativa se a M.C. nova supera a da vencedora atual": aqui a candidata entra bastando fechar o piso do grupo. A faixa de proteção do rebate (3 %) e a saída de quem anula o rebate continuam valendo (23/10/2026).</span>}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" size="small" onClick={() => void salvar()} disabled={!podeSalvar}>
              Salvar grupo
            </Button>
            {form.id || form.texto || form.nome ? (
              <Button size="small" color="inherit" onClick={() => { setForm(formVazio()); setMsg(null) }} disabled={busy}>
                limpar
              </Button>
            ) : null}
            {msg ? <Typography sx={{ fontSize: 12 }}>{msg}</Typography> : null}
          </Stack>
        </Stack>

        {lista.length ? (
          lista.map((g) => (
            <Box key={g.id} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider', fontSize: 12.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.6fr auto auto' }, gap: 1.25, alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: 13, cursor: 'pointer', fontWeight: 600 }} onClick={() => toggle(g.id)} title="mostrar/esconder os MLBs">
                    {aberto.has(g.id) ? '▾' : '▸'} 🎄 {g.nome}{g.relampago_diaria ? <Box component="span" sx={{ ml: 0.75 }}><Pill label="⚡ lista fixa da relâmpago" tone="cand" /></Box> : null}{g.liquidacao ? <Box component="span" sx={{ ml: 0.75 }}><Pill label="🔥 liquidação" tone="cand" /></Box> : null}
                    {g.preco_alvo != null ? <Box component="span" sx={{ ml: 0.75 }}><Pill label={`🎯 alvo ${brl(g.preco_alvo)}${g.tolerancia_acima != null ? ` +${g.tolerancia_acima}%` : ''}`} tone="cand" /></Box> : null}
                    {g.respeitar_desconto_manual === false ? <Box component="span" sx={{ ml: 0.75 }}><Pill label="desconto manual NÃO protegido" tone="mock" /></Box> : null}
                    {g.permitir_relampago && !g.relampago_diaria ? <Box component="span" sx={{ ml: 0.75 }}><Pill label="⚡ relâmpago permitida" /></Box> : null}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {g.n_mlbs} MLBs{g.por ? ` · por ${g.por}` : ''}{g.motivo ? ` · ${g.motivo}` : ''}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12.5 }}>pisos {rotuloPisos(g)}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {g.cupons.length ? `cupons: ${(g.cupons_nomes?.length ? g.cupons_nomes : g.cupons).join(', ')}` : 'sem cupom: estes anúncios não recebem cupom nenhum'}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {g.campanhas?.length ? `campanhas do grupo: ${(g.campanhas_nomes?.length ? g.campanhas_nomes : g.campanhas).join(', ')}` : 'sem campanha marcada'}
                  </Typography>
                  {/* dado que falta para a conta do cupom, como o motor leu (fila.grupos[].cupom.falta — ela, 06/10/2026) */}
                  {(() => {
                    const falta = ctx.fila?.grupos?.find((x) => x.id === g.id)?.cupom?.falta
                    return falta ? <Typography sx={{ fontSize: 11.5, color: LARANJA }}>⚠ {falta}</Typography> : null
                  })()}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: corEstado(g), fontWeight: g.estado === 'vigente' ? 400 : 700 }}>{rotuloEstado(g)}</Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="text" color="inherit" onClick={() => editar(g)} disabled={!ctx.escreve || busy}>
                    editar
                  </Button>
                  <Button size="small" variant="text" color="inherit" onClick={() => void renovar(g)} disabled={!ctx.escreve || busy} title={`regrava o grupo até ${ddmmaaaa(hojeMais(PRAZO_PADRAO))}`}>
                    renovar
                  </Button>
                  <Button size="small" variant={armado === g.id ? 'contained' : 'text'} color="error" onClick={() => void remover(g)} onBlur={() => setArmado((x) => (x === g.id ? null : x))} disabled={!ctx.escreve || busy}>
                    {armado === g.id ? 'confirmar remoção' : 'remover'}
                  </Button>
                </Stack>
              </Box>
              <Collapse in={aberto.has(g.id)} unmountOnExit>
                {/* prévia por MLB (ela, 25/10/2026, 2.7): preço cheio, promoção vista, origem, alvo e a próxima rodada (última fila do motor) */}
                {(() => {
                  const pv = previa[g.id]
                  return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2, pt: 0.5 }}>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                        {pv === 'carregando' ? 'carregando a prévia…' : pv === 'erro' ? 'não consegui ler a prévia' : pv ? `prévia da fila de ${pv.gerado_em ? quando(pv.gerado_em) : '?'} (conta ${pv.conta.slice(-4)})` : ctx.escreve ? '' : 'prévia só com o serviço no ar'}
                      </Typography>
                      {ctx.escreve ? <Button size="small" variant="text" color="inherit" disabled={busy} onClick={() => void atualizarPrevia(g.id)} sx={{ py: 0 }}>Atualizar agora</Button> : null}
                    </Stack>
                  )
                })()}
                <Box sx={{ pl: 2, pt: 0.5, maxHeight: 420, overflowY: 'auto' }}>
                  {g.mlbs.length ? g.mlbs.map((m) => {
                    const pv = previa[g.id]
                    const pm = pv && typeof pv === 'object' ? pv.mlbs.find((x) => x.mlb === m.mlb) : undefined
                    return (
                    <Stack key={m.mlb} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }} flexWrap="wrap" useFlexGap>
                      <Box component="span" sx={{ ...mono, fontSize: 11.5, color: 'text.disabled', minWidth: 130 }}>{m.mlb}</Box>
                      <Typography noWrap sx={{ fontSize: 12.5, cursor: 'pointer', flex: 1, minWidth: 160, '&:hover': { textDecoration: 'underline' } }} onClick={() => ctx.abrirAnuncio(m.mlb)} title={tituloDe(m) || m.mlb}>
                        {tituloDe(m) || <Box component="span" sx={{ color: 'text.disabled' }}>título não informado</Box>}
                      </Typography>
                      {m.preco_alvo != null || g.preco_alvo != null ? <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }} title="preço-alvo: o do MLB vence o do grupo">🎯 {brl(m.preco_alvo ?? (g.preco_alvo as number))}{m.preco_alvo == null ? ' (grupo)' : ''}</Box> : null}
                      {pm ? (
                        <>
                          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }} title="preço cheio (data.json)">cheio {pm.preco_cheio != null ? brl(pm.preco_cheio) : '—'}</Box>
                          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }} title="promoção ativa mais barata = a que o comprador vê">vê {pm.promo_vista ? `${pm.promo_vista.nome || pm.promo_vista.tipo} ${brl(pm.promo_vista.preco)}` : 'sem promoção'}</Box>
                          {pm.origem ? <Pill label={pm.origem === 'robo' ? 'PD do robô' : '✋ PD manual'} tone={pm.origem === 'manual' ? 'mock' : undefined} /> : null}
                          {pm.proxima.length ? pm.proxima.map((d, i) => (
                            <Box key={i} component="span" sx={{ fontSize: 11, color: d.acao === 'bloqueado' || d.acao === 'alertar' ? LARANJA : 'primary.main' }} title={d.motivo}>
                              → <b>{d.acao}</b> {d.promocao_nome || d.promocao_tipo || ''}{d.de ? ` (${precoDe(d.de)} → ${precoDe(d.para)})` : d.para?.preco != null ? ` ${brl(d.para.preco)}` : ''}{d.regra ? ` · ${rotuloRegra(d.regra)}` : ''}
                            </Box>
                          )) : <Box component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>→ nada a fazer</Box>}
                        </>
                      ) : null}
                      {emExcecao.has(m.mlb) ? <Box component="span" sx={{ fontSize: 11, color: LARANJA }}>em exceção</Box> : null}
                      <Button size="small" variant="text" color="error" onClick={() => void tirar(g, m.mlb)} disabled={!ctx.escreve || busy} sx={{ minWidth: 0, py: 0 }}>
                        tirar
                      </Button>
                    </Stack>
                    )
                  }) : <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Grupo sem MLBs.</Typography>}
                </Box>
              </Collapse>
            </Box>
          ))
        ) : (
          <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhum grupo criado.</Typography>
        )}
      </Panel>
    </Box>
  )
}
